import { FormikHelpers, useFormik } from 'formik';
import { get } from 'lodash';
import React, { useContext } from 'react';
import { Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { number, object } from 'yup';

import apiUtil from 'src/api/payments';
import CustomToast from 'src/components/CustomToast';
import FormikSubmit from 'src/components/FormikSubmit';
import { AppContext } from 'src/containers/Core/AppContext';
import { AppState } from 'src/containers/Core/AppReducer';
import { ADD_ACCOUNT_SUCCESS } from 'src/containers/Core/Messages';

import './style.scss';

type VerifyBankAccountDetailsFormProps = {
  bankDetails: BankDetails;
  onSuccess: () => void;
};

type VerifyBankAccountDetailsFormValues = {
  amount1: string;
  amount2: string;
};

const VerifyBankAccountDetailsForm: React.FunctionComponent<VerifyBankAccountDetailsFormProps> = ({
  bankDetails,
  onSuccess,
}) => {
  const { store } = useContext(AppContext);
  const { businessOnboardingDetails }: AppState = store;
  const businessUuid = get(businessOnboardingDetails, 'business.uuid');

  const initialValues: VerifyBankAccountDetailsFormValues = {
    amount1: '',
    amount2: '',
  };

  const amountValidationSchema = number()
    .required('This field cannot be empty')
    .test('valid', 'Amount must be greater than 0', value => !!value && value > 0)
    .test(
      'valid',
      'Amount can be up to 2 decimal places',
      value => !!value && value > 0 && (Math.round(value * 100) / 100).toString() === value.toString()
    );

  const validationSchema = object().shape({
    amount1: amountValidationSchema,
    amount2: amountValidationSchema,
  });

  const handleVerifyFormSubmit = async (
    data: VerifyBankAccountDetailsFormValues,
    actions: FormikHelpers<VerifyBankAccountDetailsFormValues>
  ) => {
    try {
      await apiUtil.verifyBankAccount(businessUuid, {
        ...data,
        bankAccountId: bankDetails.bankAccountId,
      });
      toast(<CustomToast type="success" description={ADD_ACCOUNT_SUCCESS} />, { className: 'success' });
      onSuccess();
    } catch (error) {
      toast(<CustomToast description={get(error, 'response.data.error')} />);
    } finally {
      actions.setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleVerifyFormSubmit,
  });

  const { handleSubmit, values, errors, touched, handleChange, isSubmitting } = formik;

  return (
    <Form className="verify-bank-account-details-form w-50" noValidate onSubmit={handleSubmit}>
      <Form.Group className="mb-2 position-relative" controlId="AccountNumber">
        <Form.Label id="amount1" className="font-size-14 required mb-0">
          Transaction Amount 1
        </Form.Label>
        <span className="input-prepend-icon text-muted">$</span>
        <Form.Control
          autoFocus={true}
          className="font-size-14 pl-4"
          value={values.amount1}
          type="number"
          placeholder="Enter amount 1"
          onChange={handleChange}
          isInvalid={touched.amount1 && !!errors.amount1}
          autoComplete="off"
          name="amount1"
          disabled={isSubmitting}
          maxLength={255}
        />
        <Form.Control.Feedback type="invalid">{errors.amount1}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-2 position-relative" controlId="AccountHolderName">
        <Form.Label id="amount2" className="font-size-14 required mb-0">
          Transaction Amount 2
        </Form.Label>
        <span className="input-prepend-icon text-muted">$</span>
        <Form.Control
          className="font-size-14 pl-4"
          value={values.amount2}
          type="number"
          placeholder="Enter amount 2"
          onChange={handleChange}
          isInvalid={touched.amount2 && !!errors.amount2}
          autoComplete="off"
          name="amount2"
          disabled={isSubmitting}
          maxLength={255}
        />
        <Form.Control.Feedback type="invalid">{errors.amount2}</Form.Control.Feedback>
      </Form.Group>
      <FormikSubmit loading={isSubmitting} text="Verify" className="mt-1 mb-3" />
    </Form>
  );
};

export default VerifyBankAccountDetailsForm;
